import React from 'react';
import { ChevronDown, ChevronUp, Check, Settings } from 'lucide-react';
import { useDesignColors } from '../../hooks/useDesignColors';

interface CRMSectionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  type: 'salesforce' | 'hubspot';
  isEnabled?: boolean;
  onClick?: () => void;
  onConfigure?: () => void;
  children?: React.ReactNode;
}

export function CRMSection({ 
  title, 
  subtitle, 
  icon, 
  type, 
  isEnabled = false,
  onClick, 
  onConfigure,
  children 
}: CRMSectionProps) {
  const { colors } = useDesignColors();
  const isExpanded = !!children;

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden transition-all shadow-lg hover:shadow-xl">
      <div
        className="w-full p-6 flex items-center justify-between hover:bg-white/10 transition-colors cursor-pointer"
        onClick={onClick}
      >
        <div className="flex items-center gap-4">
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${colors.gradientStart}30, ${colors.gradientEnd}30)`,
              boxShadow: `0 4px 15px ${colors.gradientStart}15`
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { 
              className: 'w-6 h-6',
              style: { color: colors.gradientStart } 
            })}
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <p className="text-sm text-gray-400">
              {subtitle}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {isEnabled ? (
            <span 
              className="px-3 py-1 text-xs rounded-full flex items-center font-medium"
              style={{
                background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                color: colors.gradientStart
              }}
            >
              <Check className="w-3 h-3 mr-1" />
              Active
            </span>
          ) : (
            <span className="px-3 py-1 text-xs rounded-full bg-gray-700/40 text-gray-400 font-medium">
              Inactive
            </span>
          )}
          <div className="flex items-center gap-2">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onConfigure?.();
              }}
              className="text-gray-400 hover:text-white transition-colors p-2 rounded-md hover:bg-white/10"
              title="Configure integration"
              style={{ 
                color: isExpanded ? colors.gradientStart : undefined 
              }}
            >
              <Settings className="w-5 h-5" />
            </button>
            
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onClick?.();
              }}
              className="text-gray-400 hover:text-white transition-colors p-2 rounded-md hover:bg-white/10"
              aria-label={isExpanded ? "Collapse section" : "Expand section"}
              style={{ 
                color: isExpanded ? colors.gradientStart : undefined 
              }}
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {children && (
        <div className="border-t border-white/10 p-6 bg-white/5">
          {children}
        </div>
      )}
    </div>
  );
}